import { motion } from "framer-motion";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import Twemoji from "react-twemoji";
import { ScopeType } from "sockapi/scope-types";

import { useReactiveTimeout } from "client-util/useReactiveTimeout";
import { useShallowMemo } from "client-util/useShallowMemo";
import {
  clsObj,
  defaultOverlayScrollbarsOptions,
  displayNameOrder,
} from "client-util/util";
import { Spinner } from "components/Loading";
import PuzzleCard from "components/PuzzlePage/PuzzleCard";
import SidebarWrapper from "components/PuzzlePage/SidebarWrapper";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import "overlayscrollbars/overlayscrollbars.css";
import { MovableType } from "sockapi/scope-specs/round-spec";
import { useViewTimes } from "stores/BrowserWidePersistentState";
import { useInPuzzlePage, useParsedSlug } from "stores/NavStore";
import {
  useBox,
  useGetRoundStateNonReactive,
  useMovables,
  useUpdatePieceInside,
} from "stores/RoundStore";
import {
  usePuzzleDisplayNames,
  useScopeStateResolvable,
  useTeamScopeState,
} from "stores/ScopeStateStore";
import { useTeamId } from "stores/ServerInteractionStore";
import settings from "settings";
import "./Sidebar.css";
import { useEvent } from "client-util/useEvent";

const NEW_INDICATOR_SHOW_TIME_MS = 60 * 1000; // 1 minute

function SidebarPieceCard(props: {
  preview?: boolean;
  puzData: { puzName: string; unlockTime: number };
  displayName: string;
  hasHints: boolean;
  pieceId: string;
  isActive: boolean;
  shouldScroll: boolean;
  onClick: (pieceId: string, isActive: boolean) => void;
}) {
  const {
    pieceId,
    puzData: { unlockTime },
    shouldScroll,
  } = props;
  const ref = useRef<HTMLDivElement>(null);
  const viewTimes = useViewTimes();
  const isSeen = useShallowMemo(() => viewTimes[pieceId] !== undefined);
  const isLongPastUnlockTime = useReactiveTimeout(
    unlockTime + NEW_INDICATOR_SHOW_TIME_MS
  );
  const isNew = !isSeen && !(isLongPastUnlockTime ?? false);

  useEffect(() => {
    if (!shouldScroll) return;
    if (ref.current === null) return;
    const boundingRect = ref.current.getBoundingClientRect();
    if (boundingRect.top < 0 || boundingRect.bottom > window.innerHeight)
      // This used to be smooth scrolling. For me, that causes janky animations
      // when navigating and doesn't even always end up at the right position.
      ref.current.scrollIntoView({ block: "center" });
  }, [shouldScroll]);

  return (
    <motion.div layout key={pieceId} ref={ref}>
      <PuzzleCard
        {...props}
        puzName={props.puzData.puzName}
        isNew={isNew}
        onClick={() => props.onClick(pieceId, props.isActive)}
      />
    </motion.div>
  );
}

const SidebarCards = memo(
  function SidebarCards({
    pieces,
  }: {
    pieces: {
      puzName: string;
      unlockTime: number;
    }[];
  }) {
    const teamId = useTeamId();
    const teamHintsScopeState = useScopeStateResolvable(
      ScopeType.TEAM_HINTS,
      teamId === null ? null : { teamId }
    );
    const displayNames = usePuzzleDisplayNames();
    const { inPiece, inPuzzle } = useInPuzzlePage();
    const { puzName: pagePuzName } = useParsedSlug();
    const { pieceIds } = useBox(inPuzzle ? pagePuzName : null);

    const updatePieceInside = useUpdatePieceInside();
    const onClick = useEvent((pieceId: string, isActive: boolean) => {
      if (pagePuzName) {
        void updatePieceInside({
          boxId: pagePuzName,
          pieceId: pieceId,
          add: isActive ? false : true,
        });
      }
    });

    if (
      teamId === null ||
      teamHintsScopeState === null ||
      displayNames === null
    )
      return <Spinner />;

    return (
      <>
        {pieces.map((piece) => {
          const { puzName } = piece;
          const hasHints =
            Object.keys(teamHintsScopeState?.hints[puzName] ?? {}).length > 0;
          const shouldScroll = inPiece ? pagePuzName === puzName : false;
          const isActive =
            inPuzzle && pieceIds
              ? pieceIds.includes(puzName)
              : pagePuzName === puzName;
          return (
            <SidebarPieceCard
              preview={true}
              key={puzName}
              puzData={piece}
              pieceId={puzName}
              displayName={displayNames[puzName] ?? "Locked piece"}
              hasHints={hasHints}
              shouldScroll={shouldScroll}
              isActive={isActive}
              onClick={onClick}
            />
          );
        })}
      </>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.pieces.length === nextProps.pieces.length &&
      prevProps.pieces.every(
        (p, i) =>
          p.puzName === nextProps.pieces[i].puzName &&
          p.unlockTime === nextProps.pieces[i].unlockTime
      )
    );
  }
);

const FILTERS = {
  assigned: {
    icon: "👤",
    name: "Pieces in at least one box",
  },
};

const SORTS = {
  alphabetical: "Alphabetical",
  oldest: "Oldest first",
  newest: "Newest first",
};

export function normalizeForSearch(s: string) {
  return s
    .toLowerCase()
    .replaceAll("×", "x")
    .replaceAll("’", "'")
    .replaceAll("ȷ", "j")
    .replaceAll("ó", "o")
    .replaceAll("ö", "o")
    .replaceAll("æ", "ae")
    .replaceAll("。", ".");
}

// Screw it we're doing this here too. We just need a quick and dirty way to
// save this scroll value while we're navigating around. There's still some
// jank but it's a little better???
let sidebarScroll = 0;

function Sidebar() {
  const allMovables = useMovables();
  const getRoundStateNonReactive = useGetRoundStateNonReactive();
  const teamScopeState = useTeamScopeState();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<Record<keyof typeof FILTERS, boolean>>({
    assigned: false,
  });
  const [sort, setSort] = useState<keyof typeof SORTS>("newest");
  const [showDrawer, setShowDrawer] = useState(false);
  const displayNames = usePuzzleDisplayNames();

  const pieces = useMemo(() => {
    if (
      allMovables === null ||
      teamScopeState === null ||
      displayNames === null ||
      getRoundStateNonReactive === null
    )
      return null;

    // Yes, this is non-reactive, and we're using it in a reactive context:
    // one used in rendering. That's fine, it's in a `useMemo` that will always
    // get re-evaluated whenever `allMovables` is re-evaluated, so it's fine.
    const { derived } = getRoundStateNonReactive();
    return allMovables
      .filter((movable) => {
        return movable.type === MovableType.PIECE;
      })
      .map((piece) => {
        const { id } = piece;
        const name = displayNames[id] ?? "Unknown piece";
        const puzData = teamScopeState.puzzles[id];
        const inside = derived.inside[id] ?? [];
        return { ...piece, ...puzData, name, inside };
      })
      .filter(({ name }) => {
        return normalizeForSearch(name).includes(normalizeForSearch(search));
      })
      .filter((piece) => {
        return !filter.assigned || piece.inside.length === 0;
      })
      .sort((a, b) => {
        switch (sort) {
          case "alphabetical":
            return displayNameOrder(a.name, b.name);
          case "oldest":
            return a.unlockTime - b.unlockTime;
          case "newest":
            return b.unlockTime - a.unlockTime;
        }
      })
      .map((piece) => {
        return { puzName: piece.puzName, unlockTime: piece.unlockTime };
      });
  }, [
    allMovables,
    displayNames,
    filter,
    getRoundStateNonReactive,
    search,
    sort,
    teamScopeState,
  ]);

  useEffect(() => {
    const handleClick = (ev: MouseEvent) => {
      const evTarget = ev.target as Element;
      if (!evTarget.closest(".sidebar-header")) setShowDrawer(false);
    };
    window.addEventListener("click", handleClick);
    return () => {
      window.removeEventListener("click", handleClick);
    };
  }, []);

  if (pieces === null) return null;
  if (!settings.enableSidebar) return null;
  return (
    <SidebarWrapper>
      <div className="sidebar-header">
        <input
          className="sidebar-search"
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search"
          value={search}
        />
        <button
          className={clsObj({
            "sidebar-drawer-button": true,
            active: showDrawer,
          })}
          onClick={() => setShowDrawer(!showDrawer)}
        >
          ⥥
        </button>
        {showDrawer && (
          <div className="sidebar-drawer">
            <div className="sidebar-drawer-section">
              Show:
              {Object.entries(FILTERS).map(([key, { icon, name }]) => (
                <button
                  key={key}
                  className={clsObj({
                    active: filter[key as keyof typeof filter],
                  })}
                  onClick={() =>
                    setFilter((prev) => ({
                      ...prev,
                      [key]: !prev[key as keyof typeof filter],
                    }))
                  }
                >
                  <Twemoji tag="span">{icon}</Twemoji>{" "}
                  {key === "star" ? "Stars" : name}
                </button>
              ))}
            </div>
            <div className="sidebar-drawer-section">
              Sort:
              {Object.entries(SORTS).map(([key, name]) => (
                <button
                  key={key}
                  className={clsObj({ active: sort === key })}
                  onClick={() => setSort(key as keyof typeof SORTS)}
                >
                  {name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      <OverlayScrollbarsComponent
        options={defaultOverlayScrollbarsOptions}
        events={{
          initialized: (instance) => {
            const { scrollOffsetElement } = instance.elements();
            if (scrollOffsetElement.scrollTop === 0)
              scrollOffsetElement.scrollTop = sidebarScroll;
          },
          scroll: (instance) => {
            const { scrollOffsetElement } = instance.elements();
            sidebarScroll = scrollOffsetElement.scrollTop;
          },
        }}
        defer
      >
        <div className="sidebar-cards">
          <SidebarCards pieces={pieces} />
        </div>
      </OverlayScrollbarsComponent>
    </SidebarWrapper>
  );
}

export default Sidebar;
